import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/shared/SEOHead.jsx";
import JsonLd from "../../components/shared/JsonLd.jsx";
import Breadcrumbs from "../../components/shared/Breadcrumbs.jsx";
import ResponsiveImage from "../../components/shared/ResponsiveImage.jsx";
import { buildBreadcrumbJsonLd } from "../../utils/jsonld.js";
import { SUPPORT_PATH, SUPPORT_SUBPAGE_PATH } from "./paths.js";
import { BANNER_SIZES, BANNER_VARIANTS, bannerEmbedCode, bannerFile } from "./banners.js";

const SPENDEN_PATH = { de: "spenden", en: "donate" };
const HERO_IMAGE_SRC = "/assets/brand/support.png";

const codeBlockStyle = {
  margin: 0,
  padding: "14px 16px",
  background: "rgba(10,9,7,0.5)",
  border: "1px solid rgba(254,250,239,0.12)",
  borderRadius: "8px",
  fontFamily: "var(--font-mono)",
  fontSize: "12px",
  lineHeight: 1.6,
  color: "var(--color-cream-soft)",
  // Wrapping rather than scrolling: a horizontally scrollable <pre> needs
  // its own keyboard focus to be reachable (axe:
  // scrollable-region-focusable), and hiding half the snippet off-screen
  // is the wrong answer for something meant to be read and copied whole.
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
};

/**
 * Full-size view of one banner. The 468x60 and 250x250 artworks are
 * unreadable at card width, and "does this look right on my site?" is
 * exactly the question this page has to answer before someone embeds it.
 *
 * Not rendered at all until opened, so the prerendered snapshot and the
 * client's first render agree (§ scripts/prerender.js).
 */
function BannerLightbox({ banner, onClose, t }) {
  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={banner.alt}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "20px",
        padding: "24px",
        background: "rgba(11,9,8,0.92)",
      }}
    >
      {/* On a light plate, at natural size. The banners are dark artwork
        * and will usually sit on a light partner page — showing them
        * against the same dark backdrop would hide exactly the edges
        * somebody opened this preview to judge. */}
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          background: "var(--color-cream)",
          padding: "24px",
          borderRadius: "12px",
          maxWidth: "min(100%, 96vw)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
        }}
      >
        <img
          src={banner.file}
          width={banner.width}
          height={banner.height}
          alt={banner.alt}
          style={{ maxWidth: "100%", height: "auto", display: "block", borderRadius: "4px" }}
        />
      </div>
      <span style={{ fontSize: "14px", color: "var(--color-cream-faint)" }}>
        {`${banner.width} × ${banner.height} px`}
      </span>
      <button
        type="button"
        autoFocus
        onClick={onClose}
        className="tl-pill tl-pill-outline"
        style={{ fontSize: "14px", padding: "10px 22px", cursor: "pointer" }}
      >
        {t("support.hub.closePreview")}
      </button>
    </div>
  );
}

/** One banner: preview, its two download links, and the paste-ready
 * snippet. The copy button is the whole point of shipping a snippet at
 * all — nobody hand-selects five lines of HTML out of a scroll box. */
function BannerCard({ size, variant, lang, t, onPreview }) {
  const [copied, setCopied] = useState(false);
  const code = bannerEmbedCode(size, variant, lang);
  const file = bannerFile(size.key, variant, lang);
  const alt = BANNER_VARIANTS[variant].alt[lang];

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (older browser, insecure context) — the
      // snippet is still selectable in the block below.
    }
  }

  return (
    <div className="tl-card" style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      {/* Only the size here — the h3 above the group already says which
        * variant these three are. */}
      <span style={{ fontFamily: "var(--font-serif)", fontSize: "18px", color: "var(--color-cream)" }}>
        {`${size.width} × ${size.height}`}
      </span>

      {/* Not ResponsiveImage: these are fixed-size, pixel-exact banner
        * assets, and generating srcset variants of a 300x100 PNG would
        * only invite a browser to serve a blurry one. */}
      <button
        type="button"
        onClick={() => onPreview({ file, alt, width: size.width, height: size.height })}
        aria-label={t("support.hub.openPreview", { size: `${size.width} × ${size.height}` })}
        style={{ padding: 0, border: "none", background: "none", cursor: "zoom-in", alignSelf: "flex-start", maxWidth: "100%" }}
      >
        <img
          src={file}
          width={size.width}
          height={size.height}
          alt={alt}
          loading="lazy"
          style={{ maxWidth: "100%", height: "auto", borderRadius: "6px", display: "block" }}
        />
      </button>

      <pre style={codeBlockStyle}>
        <code>{code}</code>
      </pre>

      <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "center" }}>
        <button type="button" onClick={handleCopy} className="tl-pill tl-pill-outline" style={{ fontSize: "13px", padding: "9px 18px", cursor: "pointer" }}>
          {t(copied ? "support.hub.copied" : "support.hub.copyCode")}
        </button>
        <a href={file} download style={{ fontSize: "13px" }}>
          {t("support.hub.downloadPng")}
        </a>
      </div>
    </div>
  );
}

export default function SupportHub({ lang }) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const closePreview = useCallback(() => setPreview(null), []);
  const otherLang = lang === "de" ? "en" : "de";
  const path = `/${SUPPORT_PATH[lang]}`;
  const altPath = `/${SUPPORT_PATH[otherLang]}`;
  const breadcrumbItems = [
    { name: t("nav.home"), path: "/" },
    { name: t("nav.unterstuetzen"), path },
  ];

  return (
    <>
      <SEOHead
        lang={lang}
        path={path}
        altPath={altPath}
        title={t("support.hub.metaTitle")}
        description={t("support.hub.metaDescription")}
      />
      <JsonLd data={buildBreadcrumbJsonLd(breadcrumbItems, lang)} />

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "48px", paddingTop: "32px", paddingBottom: "96px" }}>
          <Breadcrumbs items={breadcrumbItems} lang={lang} />

          <div className="tl-grid-2" style={{ display: "grid", gridTemplateColumns: "1.25fr 1fr", gap: "40px", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
              <p className="tl-eyebrow" style={{ margin: 0 }}>
                {t("nav.unterstuetzen")}
              </p>
              <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "42px", lineHeight: 1.12, color: "var(--color-cream)", margin: 0 }}>
                {t("support.hub.h1")}
              </h1>
              <p style={{ fontSize: "17px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: 0 }}>{t("support.hub.intro")}</p>
            </div>
            <div style={{ position: "relative", width: "100%", aspectRatio: "4 / 3", borderRadius: "var(--radius-card-lg)", overflow: "hidden" }}>
              <ResponsiveImage
                src={HERO_IMAGE_SRC}
                alt={t("support.hub.imageAlt")}
                priority
                sizes="(max-width: 900px) 100vw, 40vw"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          </div>

          <section style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", color: "var(--color-cream)", margin: 0 }}>
              {t("support.hub.waysTitle")}
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: "20px" }}>
              {Object.entries(SUPPORT_SUBPAGE_PATH).map(([topic, paths]) => (
                <Link
                  key={topic}
                  to={`/${lang}/${SUPPORT_PATH[lang]}/${paths[lang]}`}
                  className="tl-card tl-hover-raise"
                  style={{ display: "flex", flexDirection: "column", gap: "10px" }}
                >
                  <span style={{ fontFamily: "var(--font-serif)", fontSize: "20px", color: "var(--color-cream)" }}>
                    {t(`support.${topic}.title`)}
                  </span>
                  <span style={{ fontSize: "16px", lineHeight: 1.65, color: "var(--color-cream-soft)" }}>
                    {t(`support.${topic}.teaser`)}
                  </span>
                </Link>
              ))}
              <Link to={`/${lang}/${SPENDEN_PATH[lang]}`} className="tl-card tl-hover-raise" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <span style={{ fontFamily: "var(--font-serif)", fontSize: "20px", color: "var(--color-cream)" }}>
                  {t("nav.spenden")}
                </span>
                <span style={{ fontSize: "16px", lineHeight: 1.65, color: "var(--color-cream-soft)" }}>
                  {t("support.hub.donateTeaser")}
                </span>
              </Link>
            </div>
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", color: "var(--color-cream)", margin: 0 }}>
              {t("support.hub.bannerTitle")}
            </h2>
            {/* Full width, unlike the page's other prose: these two are
              * usage terms rather than reading copy, and a 68ch column
              * left them stacked in a narrow strip beside empty space. */}
            <p style={{ fontSize: "16px", lineHeight: 1.75, color: "var(--color-cream-soft)", margin: 0 }}>
              {t("support.hub.bannerIntro")}
            </p>
            <p style={{ fontSize: "15px", lineHeight: 1.7, color: "var(--color-cream-faint)", margin: 0 }}>
              {t("support.hub.bannerUsage")}
            </p>

            {Object.keys(BANNER_VARIANTS).map((variant) => (
              <div key={variant} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <h3 style={{ fontFamily: "var(--font-serif)", fontSize: "20px", color: "var(--color-cream)", margin: "8px 0 0" }}>
                  {t(`support.hub.variant.${variant}`)}
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: "20px" }}>
                  {BANNER_SIZES.map((size) => (
                    <BannerCard key={`${variant}-${size.key}`} size={size} variant={variant} lang={lang} t={t} onPreview={setPreview} />
                  ))}
                </div>
              </div>
            ))}

          </section>
        </div>
      </div>

      {preview && <BannerLightbox banner={preview} onClose={closePreview} t={t} />}
    </>
  );
}
